import React, { useState } from 'react'
import { supabase } from '../lib/supabase'
import { parseSramCode } from '../lib/sramLookup'
import { rockShoxTrailheadUrl } from '../lib/utils'

const EMPTY_FORM = {
  type: 'fork',
  brand: 'fox',
  serial: '',
  model: '',
  year: '',
  travel: '',
  install_length: '',
  part_number: '',
  sram_model_code: '',
  notes: '',
}
const FOX_LOOKUP_COMPONENT_TYPES = ['fork', 'shock', 'dropper']
const TRAILHEAD_BRANDS = ['rockshox', 'sram']

const COMPONENT_COPY = {
  fork: {
    section: 'Suspension Details',
    modelLabel: 'Model Name',
    modelPlaceholder: 'e.g. RockShox Pike Select Plus',
    travelLabel: 'Travel / Stroke',
    travelPlaceholder: 'e.g. 150mm',
    installLabel: 'Installation Length',
    installPlaceholder: 'e.g. 555mm',
    partLabel: 'Part Number / Model Code',
    partPlaceholder: 'Auto-filled from lookup',
    notesLabel: 'Notes / Description',
    notesPlaceholder: 'Auto-filled from lookup, or add manually',
  },
  shock: {
    section: 'Suspension Details',
    modelLabel: 'Model Name',
    modelPlaceholder: 'e.g. Fox Float X2 Factory',
    travelLabel: 'Travel / Stroke',
    travelPlaceholder: 'e.g. 210x55mm',
    installLabel: 'Installation Length',
    installPlaceholder: 'e.g. 210mm eye-to-eye',
    partLabel: 'Part Number / Model Code',
    partPlaceholder: 'Auto-filled from lookup',
    notesLabel: 'Notes / Description',
    notesPlaceholder: 'Auto-filled from lookup, or add manually',
  },
  dropper: {
    section: 'Dropper Details',
    modelLabel: 'Model Name',
    modelPlaceholder: 'e.g. Reverb AXS, Transfer, OneUp V3',
    travelLabel: 'Drop / Travel',
    travelPlaceholder: 'e.g. 170mm drop',
    installLabel: 'Insertion / Length',
    installPlaceholder: 'e.g. 31.6 x 480mm, 250mm insertion',
    partLabel: 'Part Number / Model Code',
    partPlaceholder: 'Model code or part number if known',
    notesLabel: 'Notes / Description',
    notesPlaceholder: 'Lever, routing, return speed, cartridge, service notes.',
  },
  brake: {
    section: 'Brake Details',
    modelLabel: 'Lever / Caliper Model',
    modelPlaceholder: 'e.g. SRAM Maven Ultimate, Shimano XT M8120',
    partLabel: 'Part Number / Model Code',
    partPlaceholder: 'Model code, SKU, or brake family if known',
    notesLabel: 'Brake Notes',
    notesPlaceholder: 'Lever/caliper, front/rear/pair, rotor size, pad compound, hose length, fluid type, bleed notes.',
  },
  other: {
    section: 'Component Details',
    modelLabel: 'Model Name',
    modelPlaceholder: 'e.g. Brand and model',
    travelLabel: 'Travel / Stroke',
    travelPlaceholder: 'If relevant',
    installLabel: 'Installation Length',
    installPlaceholder: 'If relevant',
    partLabel: 'Part Number / Model Code',
    partPlaceholder: 'Part number if known',
    notesLabel: 'Notes / Description',
    notesPlaceholder: 'Add any useful component details manually.',
  },
}
const PROTECTED_JOB_FIELDS = `
  id, payment_status, payment_paid_at, payment_link, stripe_checkout_session_id,
  stripe_payment_intent_id, stripe_payment_link_id, payment_reference,
  invoice_number, report_sent_at
`

function normaliseSelectValue(value) {
  return String(value || '').trim().toLowerCase()
}

function hasProtectedJobMarkers(job = {}) {
  return job.payment_status === 'paid'
    || Boolean(job.payment_paid_at)
    || Boolean(job.payment_link)
    || Boolean(job.stripe_checkout_session_id)
    || Boolean(job.stripe_payment_intent_id)
    || Boolean(job.stripe_payment_link_id)
    || Boolean(job.payment_reference)
    || Boolean(job.invoice_number)
    || Boolean(job.report_sent_at)
}

async function hasProtectedJobHistory(jobIds = []) {
  if (!jobIds.length) return false
  const checks = [
    supabase.from('job_report_versions').select('id', { count: 'exact', head: true }).in('job_id', jobIds),
    supabase.from('audit_events').select('id', { count: 'exact', head: true }).eq('entity_type', 'job').in('entity_id', jobIds),
    supabase.from('notification_log').select('id', { count: 'exact', head: true }).in('job_id', jobIds),
  ]
  const results = await Promise.all(checks)
  const failed = results.find(result => result.error)
  if (failed) throw failed.error
  return results.some(result => (result.count || 0) > 0)
}

export default function ComponentModal({ workshopId, customerId, onClose, onSaved, onDeleted, initialData, prefillData = null }) {
  const isEdit = !!initialData
  const [form, setForm] = useState(initialData ? {
    type: normaliseSelectValue(initialData.type) || 'fork',
    brand: normaliseSelectValue(initialData.brand) || 'fox',
    serial: initialData.serial || '',
    model: initialData.model || '',
    year: initialData.year || '',
    travel: initialData.travel || '',
    install_length: initialData.install_length || '',
    part_number: initialData.part_number || '',
    sram_model_code: initialData.sram_model_code || '',
    notes: initialData.notes || ''
  } : {
    ...EMPTY_FORM,
    ...(prefillData || {}),
    type: normaliseSelectValue(prefillData?.type) || prefillData?.type || EMPTY_FORM.type,
    brand: normaliseSelectValue(prefillData?.brand) || prefillData?.brand || EMPTY_FORM.brand,
  })
  const [lookupState, setLookupState] = useState(null)
  const [lookupResult, setLookupResult] = useState(null)
  const [lookupMsg, setLookupMsg] = useState('')
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [error, setError] = useState(null)

  function set(k, v) { setForm(f => ({ ...f, [k]: v })) }

  const componentType = normaliseSelectValue(form.type) || 'fork'
  const componentCopy = COMPONENT_COPY[componentType] || COMPONENT_COPY.other
  const canFoxLookup = form.brand === 'fox' && FOX_LOOKUP_COMPONENT_TYPES.includes(componentType)
  const canTrailheadLookup = TRAILHEAD_BRANDS.includes(form.brand)
  const trailheadBrandLabel = form.brand === 'sram' ? 'SRAM' : 'RockShox'
  const isBrake = componentType === 'brake'

  async function lookupHeaders() {
    const { data: sessionData } = await supabase.auth.getSession()
    const token = sessionData?.session?.access_token
    return token ? { Authorization: `Bearer ${token}` } : {}
  }

  function onBrandChange(brand) {
    setForm(f => ({ ...f, brand }))
    setLookupState(null)
    setLookupResult(null)
    setLookupMsg('')
  }

  function onTypeChange(type) {
    setForm(f => ({
      ...f,
      type,
    }))
    setLookupState(null)
    setLookupResult(null)
    setLookupMsg('')
  }

  async function doFoxLookup() {
    if (!canFoxLookup) return
    const serial = form.serial.trim()
    if (!serial) { setLookupMsg('Enter a serial number first.'); setLookupState('error'); return }
    setLookupState('loading')
    setLookupMsg('')
    try {
      const res = await fetch('/.netlify/functions/fox-lookup?serial=' + encodeURIComponent(serial), {
        headers: await lookupHeaders(),
      })
      const data = await res.json()
      if (!data.found) {
        setLookupState('error')
        setLookupMsg(data.error || 'No product found. Fill in details manually below.')
        return
      }
      setForm(f => ({
        ...f,
        model: data.productName || f.model,
        part_number: data.partNo || f.part_number,
        notes: data.description || f.notes,
        year: data.year || f.year,
        travel: data.travel || f.travel
      }))
      setLookupResult(data)
      setLookupState('found')
      setLookupMsg(data.productName)
    } catch {
      setLookupState('error')
      setLookupMsg('Lookup failed. Fill in details manually.')
    }
  }

  async function doTrailheadLookup() {
    if (!canTrailheadLookup) return
    const serial = form.serial.trim()
    if (!serial) {
      setLookupMsg(`Enter a ${trailheadBrandLabel} serial number first.`)
      setLookupState('error')
      return
    }

    setLookupState('loading')
    setLookupMsg('')
    try {
      const res = await fetch('/.netlify/functions/rockshox-trailhead-lookup?serial=' + encodeURIComponent(serial), {
        headers: await lookupHeaders(),
      })
      const data = await res.json()
      if (!res.ok || !data.found) {
        setLookupState('error')
        setLookupMsg(data.error || 'No Trailhead product found. Fill in details manually below.')
        return
      }

      const parsed = data.modelCode ? parseSramCode(data.modelCode) : null
      setForm(f => ({
        ...f,
        serial,
        brand: f.brand === 'sram' ? 'sram' : 'rockshox',
        type: data.type || parsed?.type || f.type,
        model: data.productName || parsed?.model || f.model,
        year: data.year || parsed?.year || f.year,
        travel: data.travel || parsed?.travel || f.travel,
        install_length: data.installLength || f.install_length,
        part_number: data.partNo || data.modelCode || parsed?.part_number || f.part_number,
        sram_model_code: data.modelCode || f.sram_model_code,
        notes: data.description || parsed?.notes || f.notes,
      }))
      setLookupResult(data)
      setLookupState('found')
      setLookupMsg(data.productName || data.modelCode || 'Trailhead product found')
    } catch {
      setLookupState('error')
      setLookupMsg('Trailhead lookup failed. Open Trailhead or fill in details manually.')
    }
  }

  async function saveTrailheadServiceKits(component) {
    const serviceKits = lookupResult?.serviceKits || []
    const modelCode = String(component?.sram_model_code || component?.part_number || '').toUpperCase().trim()
    if (!workshopId || !modelCode || serviceKits.length === 0) return

    const modelName = `${component?.year ? component.year + ' ' : ''}${component?.model || modelCode}`.trim()

    for (const kit of serviceKits) {
      const partNumber = String(kit.partNumber || '').toUpperCase().trim()
      const description = String(kit.description || kit.name || '').trim()
      if (!partNumber || !description) continue

      const { data: existing } = await supabase
        .from('kit_library')
        .select('id')
        .eq('model_code', modelCode)
        .eq('part_number', partNumber)
        .maybeSingle()

      if (existing?.id) continue

      await supabase
        .from('kit_library')
        .insert({
          model_code: modelCode,
          model_name: modelName,
          part_number: partNumber,
          description,
          contributed_by: workshopId,
        })
    }
  }

  async function save() {
    const serial = form.serial.trim()
    const model = form.model.trim()
    if (!model && !(canTrailheadLookup && serial)) {
      setError(canTrailheadLookup ? `Enter a ${trailheadBrandLabel} serial number or model name.` : 'Model name required.')
      return
    }
    let inferredBikeId = initialData?.bike_id || prefillData?.bike_id || null
    if (!inferredBikeId && workshopId && customerId) {
      const { data: customerBikes } = await supabase
        .from('bikes')
        .select('id')
        .eq('workshop_id', workshopId)
        .eq('customer_id', customerId)
        .limit(2)
      if ((customerBikes || []).length === 1) inferredBikeId = customerBikes[0].id
    }

    const payload = {
      ...form,
      type: normaliseSelectValue(form.type) || 'fork',
      brand: normaliseSelectValue(form.brand) || 'fox',
      serial,
      model: model || `RockShox component ${serial}`,
    }
    if (inferredBikeId) payload.bike_id = inferredBikeId
    setSaving(true)
    setError(null)
    let err
    let saved
    if (isEdit) {
      const result = await supabase.from('components').update(payload).eq('id', initialData.id).select('*').single()
      err = result.error
      saved = result.data
    } else {
      const result = await supabase.from('components').insert({
        workshop_id: workshopId, customer_id: customerId, ...payload
      }).select('*').single()
      err = result.error
      saved = result.data
    }
    setSaving(false)
    if (err) setError(err.message)
    else {
      try {
        await saveTrailheadServiceKits(saved || payload)
      } catch (kitErr) {
        console.warn('Trailhead service kit save failed', kitErr)
      }
      onSaved(saved)
    }
  }

  async function doDelete() {
    setDeleting(true)
    setError(null)
    const { data: jobs, error: jobsError } = await supabase
      .from('jobs')
      .select(PROTECTED_JOB_FIELDS)
      .eq('component_id', initialData.id)
    if (jobsError) {
      setDeleting(false)
      setError(jobsError.message)
      return
    }
    const jobRows = jobs || []
    const protectedByMarker = jobRows.some(hasProtectedJobMarkers)
    const protectedByHistory = !protectedByMarker && await hasProtectedJobHistory(jobRows.map(job => job.id))
    if (protectedByMarker || protectedByHistory) {
      setDeleting(false)
      setConfirmDelete(false)
      setError('This component is linked to protected service, payment, invoice, report, or audit history. Keep the component record to preserve job context.')
      return
    }

    const { error: err } = await supabase.from('components').delete().eq('id', initialData.id)
    setDeleting(false)
    if (err) setError(err.message)
    else onDeleted()
  }

  const trailheadUrl = rockShoxTrailheadUrl(form.serial)

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <div className="modal-title">{isEdit ? 'Edit Component' : 'Add Component'}</div>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        {error && <div className="alert alert-error" style={{ marginBottom: 14 }}>{error}</div>}

        <div className="grid-2">
          <div className="field"><label>Type</label>
            <select value={form.type} onChange={e => onTypeChange(e.target.value)}>
              <option value="fork">Fork</option>
              <option value="shock">Rear Shock</option>
              <option value="dropper">Dropper Post</option>
              <option value="brake">Brake</option>
              <option value="drivetrain">Drivetrain</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div className="field"><label>Brand</label>
            <select value={form.brand} onChange={e => onBrandChange(e.target.value)}>
              <option value="fox">Fox</option>
              <option value="rockshox">RockShox</option>
              <option value="ohlins">Öhlins</option>
              <option value="marzocchi">Marzocchi</option>
              <option value="sram">SRAM</option>
              <option value="shimano">Shimano</option>
              <option value="other">Other</option>
            </select>
          </div>
        </div>

        {canFoxLookup && (
          <div className="field">
            <label>Serial Number</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input className="mono" value={form.serial} onChange={e => set('serial', e.target.value)} placeholder="e.g. 763993-0039-t" style={{ flex: 1 }} onKeyDown={e => e.key === 'Enter' && doFoxLookup()} />
              <button className="btn btn-primary" onClick={doFoxLookup} disabled={lookupState === 'loading'}>
                {lookupState === 'loading' ? <><span className="spinner" />&nbsp;Looking up…</> : 'Look Up'}
              </button>
            </div>
            {lookupState === 'found' && (
              <div className="lookup-result" style={{ marginTop: 10 }}>
                <div className="lookup-result-name">{lookupMsg}</div>
                {form.part_number && <div className="lookup-result-meta">Part No. {form.part_number}</div>}
                {form.notes && <div className="lookup-result-desc">{form.notes.slice(0, 200)}</div>}
                <div style={{ fontSize: 12, color: 'var(--green)', marginTop: 6 }}>✓ Details auto-filled — confirm or edit below</div>
              </div>
            )}
            {lookupState === 'error' && <div style={{ fontSize: 12, color: 'var(--red)', marginTop: 6 }}>{lookupMsg}</div>}
          </div>
        )}

        {canTrailheadLookup && (
          <>
            <div className="field">
              <label>{trailheadBrandLabel} Serial Number</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input className="mono" value={form.serial} onChange={e => { set('serial', e.target.value); setLookupState(null); setLookupResult(null) }} placeholder="e.g. 28T33952674" style={{ flex: 1 }} onKeyDown={e => e.key === 'Enter' && doTrailheadLookup()} />
                <button className="btn btn-primary" onClick={doTrailheadLookup} disabled={lookupState === 'loading' || !form.serial.trim()}>
                  {lookupState === 'loading' ? <><span className="spinner" />&nbsp;Looking up...</> : 'Look Up'}
                </button>
                <a className="btn btn-ghost" href={trailheadUrl} target="_blank" rel="noopener noreferrer">Open</a>
              </div>
              <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 5 }}>
                Look up by serial to prefill the component, or open Trailhead to check product details and support docs.
              </div>
            </div>
            {lookupState === 'found' && lookupResult && (
              <div className="lookup-result" style={{ marginTop: 4, marginBottom: 12 }}>
                <div className="lookup-result-name">{lookupMsg}</div>
                {form.year && <div className="lookup-result-meta">Year: {form.year}</div>}
                {!isBrake && form.travel && <div className="lookup-result-meta">Travel range: {form.travel}</div>}
                {lookupResult.serviceKits?.length > 0 && (
                  <div style={{ marginTop: 10 }}>
                    <div className="lookup-result-meta" style={{ color: 'var(--muted)', fontWeight: 700, textTransform: 'uppercase' }}>Service kit</div>
                    {lookupResult.serviceKits.map(kit => (
                      <div key={kit.partNumber} style={{ marginTop: 4 }}>
                        <div className="lookup-result-meta" style={{ color: 'var(--white)', fontFamily: 'monospace' }}>{kit.partNumber}</div>
                        <div className="lookup-result-desc">{kit.description || kit.name}</div>
                      </div>
                    ))}
                  </div>
                )}
                <div style={{ fontSize: 12, color: 'var(--green)', marginTop: 6 }}>✓ Details auto-filled — confirm or edit below</div>
                <a href={trailheadUrl} target="_blank" rel="noopener noreferrer" className="btn btn-ghost btn-sm" style={{ marginTop: 10, display: 'inline-flex' }}>Open Trailhead</a>
              </div>
            )}
            {lookupState === 'error' && <div style={{ fontSize: 12, color: 'var(--red)', marginBottom: 12 }}>{lookupMsg}</div>}
          </>
        )}

        {!canFoxLookup && !canTrailheadLookup && (
          <div className="field">
            <label>{isBrake ? 'Serial / Caliper Code' : 'Serial Number'}</label>
            <input className="mono" value={form.serial} onChange={e => set('serial', e.target.value)} placeholder={isBrake ? 'For records, if present' : 'For records'} />
          </div>
        )}

        <hr />
        <div className="sec" style={{ marginTop: 0 }}>{componentCopy.section}</div>
        {isBrake && (
          <div className="alert alert-info" style={{ marginBottom: 14 }}>
            Brake-specific detail is captured in the existing notes fields for now. Record lever/caliper, front/rear/pair, rotor, pad, hose and fluid details here.
          </div>
        )}
        <div className="grid-2">
          <div className="field"><label>{componentCopy.modelLabel}</label><input value={form.model} onChange={e => set('model', e.target.value)} placeholder={componentCopy.modelPlaceholder} /></div>
          <div className="field"><label>Year</label><input value={form.year} onChange={e => set('year', e.target.value)} placeholder="2024" /></div>
        </div>
        {!isBrake && (
          <div className="grid-2">
            <div className="field"><label>{componentCopy.travelLabel}</label><input value={form.travel} onChange={e => set('travel', e.target.value)} placeholder={componentCopy.travelPlaceholder} /></div>
            <div className="field"><label>{componentCopy.installLabel}</label><input value={form.install_length} onChange={e => set('install_length', e.target.value)} placeholder={componentCopy.installPlaceholder} /></div>
          </div>
        )}
        <div className="field"><label>{componentCopy.partLabel}</label><input className="mono" value={form.part_number} onChange={e => set('part_number', e.target.value)} placeholder={componentCopy.partPlaceholder} /></div>
        <div className="field"><label>{componentCopy.notesLabel}</label><textarea value={form.notes} onChange={e => set('notes', e.target.value)} placeholder={componentCopy.notesPlaceholder} /></div>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn btn-primary" onClick={save} disabled={saving || deleting}>{saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Save Component'}</button>
            <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          </div>
          {isEdit && !confirmDelete && (
            <button className="btn btn-ghost btn-sm" style={{ color: 'var(--red)', borderColor: 'var(--red)' }} onClick={() => setConfirmDelete(true)}>Delete Component</button>
          )}
          {isEdit && confirmDelete && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span className="fs12 text-muted">Delete this component if it has no protected job history?</span>
              <button className="btn btn-sm" style={{ background: 'var(--red)', color: '#fff' }} onClick={doDelete} disabled={deleting}>{deleting ? 'Deleting…' : 'Yes, delete'}</button>
              <button className="btn btn-ghost btn-sm" onClick={() => setConfirmDelete(false)}>No</button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
